import { Injectable } from '@angular/core';
import { ActivatedRouteSnapshot, CanActivate, Router, RouterStateSnapshot } from '@angular/router';
import { Store } from '@ngrx/store';
import { CoreUiPartialState, getArbCentralNavSecurity } from '@pos-app/core-ui';
import { Observable } from 'rxjs';
import { filter, map } from 'rxjs/operators';
import { ToastrService } from 'ngx-toastr';
import { ArbCentralNavSecurity } from '@pos-app/data';

@Injectable({
  providedIn: 'root',
})
export class ArbCentralSecurityGuard implements CanActivate {
  private readonly permissionMappings: { regex: RegExp; permissionKey: keyof ArbCentralNavSecurity }[] = [
    { regex: /\/central\/task.*$/, permissionKey: 'manageTicketsActionTF' },
    { regex: /\/epc.*$/, permissionKey: 'productCatalogueActionTF' },
    { regex: /\/pos\/my-orders\/pos-arb-orders.*$/, permissionKey: 'jobRequestActionTF' },
    { regex: /\/pos\/scheduler.*$/, permissionKey: 'workshopBookingActionTF' },
    { regex: /\/pos\/manageusers\/external.*$/, permissionKey: 'manageRegisteredUsersActionTF' },
    { regex: /\/pos\/register\/?$/, permissionKey: 'registerAdminActionTF' },
    { regex: /\/pos\/manageusers\/internal.*$/, permissionKey: 'manageInternalUsersActionTF' },
    { regex: /\/pos\/partner.*$/, permissionKey: 'managePartnersActionTF' },
    { regex: /\/pos\/manageusers\/show.*$/, permissionKey: 'manageShowUsersActionTF' },
    { regex: /\/pos\/customers\/search.*$/, permissionKey: 'customerSearchActionTF' },
    { regex: /\/pos\/manageusers\/profile.*$/, permissionKey: 'manageMyProfileActionTF' },
    { regex: /\/stock\/search.*$/, permissionKey: 'itemPriceSearchActionTF' },
    { regex: /\/central\/manage\/how-to-help-component.*$/, permissionKey: 'manageRelatedSitesActionTF' },
  ];
  constructor(private store: Store<CoreUiPartialState>, private router: Router, private toastr: ToastrService) {}

  canActivate(route: ActivatedRouteSnapshot, state: RouterStateSnapshot): Observable<boolean> | Promise<boolean> | boolean {
    return this.store.select(getArbCentralNavSecurity).pipe(
      map((arbCentralNavSecurity: ArbCentralNavSecurity) => {
        if (!arbCentralNavSecurity) {
          return false; // Wait for the value to be emitted
        }

        const hasPermission = this.checkPermissions(state.url, arbCentralNavSecurity);

        if (!hasPermission) {
          this.toastr.error('You do not have permission to access this page');
          this.router.navigateByUrl(this.router.url);
        }

        return hasPermission;
      }),
      filter((value) => value !== false) // Ensure the observable doesn't complete until a value is emitted
    );
  }

  private checkPermissions(url: string, security: ArbCentralNavSecurity): boolean {
    return this.permissionMappings.some(({ regex, permissionKey }) => regex.test(url) && security[permissionKey]);
  }
}
